// ---------- une partie : les couloirs, le ruban du mot mystère, le clavier du jeu ----------
//
// La maquette « I ». Chaque ligne est un couloir de toute la largeur : ses quatre indices en haut,
// ses cases dessous, décalées pour que la lettre du mystère tombe dans la colonne du ruban
// (shared/mystery.ts calcule le décalage côté serveur, `PuzzleLine.offset`). Toucher n'importe où
// dans un couloir choisit son mot.
//
// Le clavier est CELUI DU JEU, pas celui du téléphone : il ne se ferme pas à chaque perte de
// focus, ne pousse pas la page, n'a que A–Z, et porte la touche « sens » qui passe d'une ligne au
// mot mystère sur une case de la colonne. Les cases ne sont donc pas des <input> : ce sont des
// boutons, et la saisie passe par `type()` — depuis le clavier du jeu, ou un clavier physique.
//
// `render` construit une fois ; `paint` ne bascule que des classes et des lettres (cf. CLAUDE.md,
// « Le DOM ») : reconstruire sur chaque lettre couperait les animations en plein vol.

import { gridWord } from '../shared/normalize';
import type { PuzzlePayload } from '../shared/types';
import { watchRewarded } from './ads';
import { coach, dismissCoach } from './coach';
import { settings } from './prefs';
import { burst } from './fx';
import { t } from './i18n';
import { buzz } from './platform';
import { persist, progress, type PuzzleProgress } from './store';
import { $, esc, ICON, toast, topBar } from './ui';

export interface PlayHooks {
  close(): void;
  finished(p: PuzzlePayload): void;
}

/** Les deux claviers du jeu (Réglages → Clavier). Même forme — 10, 10, 6 —, pour que la touche
 *  « sens » et l'effacement restent à leur place quand on change de disposition. */
const LAYOUTS = {
  azerty: ['AZERTYUIOP', 'QSDFGHJKLM', 'WXCVBN'],
  abc: ['ABCDEFGHIJ', 'KLMNOPQRST', 'UVWXYZ'],
};

let pz: PuzzlePayload;
let st: PuzzleProgress;
let hooks: PlayHooks;
let words: string[] = [];
/** le curseur : la ligne, la lettre DANS le mot, et le sens (la ligne, ou la colonne du mystère) */
let cur = { i: 0, j: 0, mys: false };
let mysteryShown = false;
let ended = false;
let wired = false;
/** Le délai entre la dernière ligne juste et l'écran de fin. Retenu pour être annulé : quitter
 *  la partie pendant ces 650 ms (la croix, le Retour d'Android) ouvrait sinon l'écran de fin
 *  PAR-DESSUS le chemin, et rouvrir une autre partie dans l'intervalle la terminait avec les
 *  données de la nouvelle (revue de code, 2026-10-02). */
let finishTimer = 0;

const key = (i: number, j: number): string => `${i},${j}`;
/** La place, dans le mot de la ligne `i`, de la lettre qui tombe dans le ruban. */
const pickOf = (i: number): number => pz.lines[i].pick;
const solved = (i: number): boolean => [...words[i]].every((ch, j) => st.e[key(i, j)] === ch);
const allSolved = (): boolean => words.every((_, i) => solved(i));
const mysteryDone = (): boolean => words.every((w, i) => st.e[key(i, pickOf(i))] === w[pickOf(i)]);

/** Quitter la partie de l'extérieur (le bouton Retour d'Android) : comme la croix. */
export function closePuzzle(): void {
  clearTimeout(finishTimer);
  dismissCoach();
  hooks.close();
}

export function openPuzzle(p: PuzzlePayload, line: string, h: PlayHooks): void {
  clearTimeout(finishTimer);
  pz = p;
  hooks = h;
  st = progress(p.id);
  words = p.lines.map((l) => l.enigma.word);
  mysteryShown = mysteryDone();
  ended = false;
  const first = words.findIndex((_, i) => !solved(i));
  cur = { i: Math.max(0, first), j: 0, mys: false };
  cur.j = firstEmpty(cur.i);
  render(line);
  if (!wired) {
    wired = true;
    $('play').addEventListener('click', onClick);
    document.addEventListener('keydown', onKey);
  }
  paint();
  coach($('play'));
}

function firstEmpty(i: number): number {
  for (let j = 0; j < words[i].length; j++) if (!st.e[key(i, j)]) return j;
  return 0;
}

function cluesOf(i: number): string {
  const e = pz.lines[i].enigma;
  const paid = e.hints.slice(0, st.h[i] || 0);
  return (
    e.clues.map((c) => `<span>${esc(c)}</span>`).join('<i>·</i>') +
    paid.map((c) => `<i>·</i><span class="paid">${esc(c)}</span>`).join('')
  );
}

function render(line: string): void {
  const cols = pz.width;
  const ROWS = LAYOUTS[settings().keyboard];
  $('play').innerHTML = `
    ${topBar(line, `<button class="icon-btn" type="button" data-act="close" aria-label="${t('bar.close')}">${ICON.close}</button>`)}
    <div class="lanes-box">
      <div class="lanes" style="--cols:${cols};--col:${pz.col}">
        <div class="spine" aria-hidden="true"></div>
        ${pz.lines
          .map((l, i) => {
            const w = words[i];
            let cells = '';
            for (let c = 0; c < cols; c++) {
              const j = c - l.offset;
              cells +=
                j >= 0 && j < w.length
                  ? `<button type="button" class="cell${c === pz.col ? ' col' : ''}" data-i="${i}" data-j="${j}" aria-label="${t('play.cell', { r: i + 1, c: j + 1 })}${c === pz.col ? ' (' + t('play.mystery') + ')' : ''}"></button>`
                  : '<span class="gap"></span>';
            }
            return `<div class="lane" data-lane="${i}">
              <button type="button" class="lane-bg" data-act="lane" data-i="${i}" aria-label="${esc(t('play.lane', { clues: l.enigma.clues.join(', ') }))}"></button>
              <div class="clues" data-act="lane" data-i="${i}">${cluesOf(i)}</div>
              <div class="cells">${cells}</div>
            </div>`;
          })
          .join('')}
      </div>
    </div>
    <div class="actions">
      <button class="btn btn-hint" type="button" data-act="hint">${ICON.play}<span class="hint-label"></span><span class="ad">${t('play.ad')}</span></button>
      <button class="btn btn-reveal" type="button" data-act="reveal">${ICON.eye}${t('play.reveal')}</button>
    </div>
    <div class="kbd" role="group" aria-label="${t('play.keyboard')}">
      ${ROWS.slice(0, 2)
        .map(
          (r) =>
            `<div class="krow">${[...r].map((ch) => `<button type="button" class="key" data-key="${ch}">${ch}</button>`).join('')}</div>`,
        )
        .join('')}
      <div class="krow">
        <button type="button" class="key key-fn" data-act="dir" aria-label="${t('play.dirAria')}"><b class="dir-glyph">→</b><small>${t('play.dir')}</small></button>
        ${[...ROWS[2]].map((ch) => `<button type="button" class="key" data-key="${ch}">${ch}</button>`).join('')}
        <button type="button" class="key key-fn" data-act="erase" aria-label="${t('play.erase')}">${ICON.erase}</button>
      </div>
    </div>`;
}

/** Ne bascule que des classes et des lettres : la page ne se reconstruit pas sous le doigt. */
function paint(): void {
  const root = $('play');
  const mys = mysteryDone();
  root.querySelector('.spine')!.className = `spine${mys ? ' ok' : cur.mys ? ' on' : ''}`;
  root.querySelectorAll<HTMLElement>('.lane').forEach((lane) => {
    const i = Number(lane.dataset.lane);
    lane.classList.toggle('on', !cur.mys && i === cur.i);
    lane.classList.toggle('solved', solved(i));
  });
  root.querySelectorAll<HTMLButtonElement>('.cell').forEach((cell) => {
    const i = Number(cell.dataset.i);
    const j = Number(cell.dataset.j);
    const k = key(i, j);
    cell.textContent = st.e[k] || '';
    // L'aria-label masque le contenu du bouton : sans la lettre dedans, un lecteur d'écran
    // annonçait « ligne 2, case 3 » sur une case pleine comme sur une vide.
    cell.dataset.a ??= cell.getAttribute('aria-label') ?? '';
    cell.setAttribute('aria-label', st.e[k] ? `${cell.dataset.a} : ${st.e[k]}` : cell.dataset.a);
    cell.setAttribute('aria-current', String(i === cur.i && j === cur.j));
    const inWord = cur.mys ? j === pickOf(i) : i === cur.i;
    cell.classList.toggle('in', inWord);
    cell.classList.toggle('cur', i === cur.i && j === cur.j);
    cell.classList.toggle('ok', solved(i) && !st.g[k]);
    cell.classList.toggle('given', !!st.g[k]);
    cell.classList.toggle('mys-ok', mys && j === pickOf(i));
  });
  const onLine = !cur.mys && !solved(cur.i);
  const h = st.h[cur.i] || 0;
  const hint = root.querySelector<HTMLButtonElement>('.btn-hint')!;
  hint.disabled = !onLine || h >= 2;
  hint.querySelector('.hint-label')!.textContent =
    h < 2 ? t('play.hint', { n: h + 1 }) : t('play.noHint');
  root.querySelector<HTMLButtonElement>('.btn-reveal')!.disabled = !onLine;
  const dir = root.querySelector<HTMLButtonElement>('[data-act="dir"]')!;
  dir.disabled = cur.j !== pickOf(cur.i);
  dir.querySelector('.dir-glyph')!.textContent = cur.mys ? '↓' : '→';
  // Le couloir en cours reste en vue quand la pile défile (maquette « I », huit lignes et plus).
  root
    .querySelector(`.lane[data-lane="${cur.i}"]`)
    ?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
}

const locked = (i: number, j: number): boolean => solved(i) || !!st.g[key(i, j)];

/** La case suivante à remplir : dans la ligne, la prochaine vide ; dans le mystère, la case de
 *  colonne vide de la ligne suivante. Rien de vide : on ne bouge pas. */
function advance(): void {
  if (cur.mys) {
    for (let d = 1; d <= words.length; d++) {
      const i = (cur.i + d) % words.length;
      if (!st.e[key(i, pickOf(i))]) return void (cur = { i, j: pickOf(i), mys: true });
    }
    return;
  }
  const w = words[cur.i];
  for (let j = cur.j + 1; j < w.length; j++) if (!st.e[key(cur.i, j)]) return void (cur.j = j);
  for (let j = 0; j < cur.j; j++) if (!st.e[key(cur.i, j)]) return void (cur.j = j);
}

function type(ch: string): void {
  const { i, j } = cur;
  if (locked(i, j)) {
    advance();
    return paint();
  }
  st.e[key(i, j)] = ch;
  persist();
  advance();
  paint();
  // En ligne comme dans le mystère, une lettre ne touche qu'une case, donc qu'une ligne.
  settle(i);
  checkMystery();
}

/** Une ligne pleine se juge d'elle-même : juste, elle passe au vert et s'éclate en confettis ;
 *  fausse, elle tremble — pas de bouton « vérifier », c'est la saisie qui répond. */
function settle(i: number): void {
  const full = [...words[i]].every((_, j) => st.e[key(i, j)]);
  if (!full) return;
  const lane = $('play').querySelector<HTMLElement>(`.lane[data-lane="${i}"]`)!;
  if (solved(i)) {
    const r = lane.getBoundingClientRect();
    burst(r.left + r.width / 2, r.top + r.height / 2, 30, 0.8);
    void buzz();
    toast(t('play.found', { word: pz.lines[i].enigma.answer.toUpperCase() }));
    if (!cur.mys && cur.i === i) {
      const next = words.findIndex((_, k) => !solved(k));
      if (next >= 0) cur = { i: next, j: firstEmpty(next), mys: false };
    }
    paint();
    if (allSolved() && !ended) {
      ended = true;
      dismissCoach();
      // Le puzzle et les crochets de CE moment, pas ceux qui seront en place dans 650 ms.
      const done = pz;
      const h = hooks;
      finishTimer = window.setTimeout(() => h.finished(done), 650);
    }
  } else {
    lane.classList.remove('wrong');
    void lane.offsetWidth;
    lane.classList.add('wrong');
  }
}

function checkMystery(): void {
  if (mysteryShown || !mysteryDone()) return;
  mysteryShown = true;
  toast(t('play.mysteryFound'));
  const spine = $('play').querySelector('.spine')!.getBoundingClientRect();
  burst(spine.left + spine.width / 2, spine.top + 40, 50, 1);
  paint();
}

function erase(): void {
  const { i, j } = cur;
  const k = key(i, j);
  if (st.e[k] && !locked(i, j)) delete st.e[k];
  else if (cur.mys) {
    for (let d = 1; d <= words.length; d++) {
      const p = (i - d + words.length) % words.length;
      if (!locked(p, pickOf(p))) {
        cur = { i: p, j: pickOf(p), mys: true };
        delete st.e[key(p, pickOf(p))];
        break;
      }
    }
  } else if (j > 0 && !locked(i, j - 1)) {
    cur.j = j - 1;
    delete st.e[key(i, j - 1)];
  }
  persist();
  paint();
}

function pickLane(i: number): void {
  cur = { i, j: firstEmpty(i), mys: false };
  paint();
}

function pickCell(i: number, j: number): void {
  // Toucher deux fois la case du ruban, c'est changer de sens : la ligne, puis le mot mystère.
  const onCol = j === pickOf(i);
  if (onCol && cur.i === i && cur.j === j) cur.mys = !cur.mys;
  else cur = { i, j, mys: onCol && cur.mys };
  paint();
}

async function hint(): Promise<void> {
  const i = cur.i;
  if (cur.mys || solved(i) || (st.h[i] || 0) >= 2) return;
  // La publicité dure trente secondes : le joueur a pu quitter, ou ouvrir une autre partie.
  // L'indice va à la ligne et à la partie où il a été demandé, ou nulle part.
  const asked = pz;
  if (!(await watchRewarded()) || pz !== asked || $('play').hidden) return;
  st.h[i] = (st.h[i] || 0) + 1;
  persist();
  const clues = $('play').querySelector<HTMLElement>(`.lane[data-lane="${i}"] .clues`)!;
  clues.innerHTML = cluesOf(i);
  clues.lastElementChild?.classList.add('fresh');
  paint();
}

function reveal(): void {
  const i = cur.i;
  if (cur.mys || solved(i)) return;
  [...words[i]].forEach((ch, j) => {
    const k = key(i, j);
    if (st.e[k] !== ch) st.g[k] = 1;
    st.e[k] = ch;
  });
  st.r[i] = 1;
  persist();
  paint();
  settle(i);
  checkMystery();
}

function onClick(e: Event): void {
  const el = (e.target as HTMLElement).closest<HTMLElement>('[data-act], [data-key], .cell');
  if (!el) return;
  if (el.dataset.key) return type(el.dataset.key);
  if (el.classList.contains('cell')) return pickCell(Number(el.dataset.i), Number(el.dataset.j));
  switch (el.dataset.act) {
    case 'close':
      return closePuzzle();
    case 'lane':
      return pickLane(Number(el.dataset.i));
    case 'dir':
      if (cur.j === pickOf(cur.i)) {
        cur.mys = !cur.mys;
        paint();
      }
      return;
    case 'erase':
      return erase();
    case 'hint':
      return void hint();
    case 'reveal':
      return reveal();
  }
}

/** Un clavier physique (web, tablette) : les lettres, l'effacement, et les flèches. */
function onKey(e: KeyboardEvent): void {
  if ($('play').hidden || e.ctrlKey || e.metaKey || e.altKey) return;
  if (e.key === 'Backspace') {
    e.preventDefault();
    return erase();
  }
  if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
    e.preventDefault();
    const n = (cur.i + (e.key === 'ArrowDown' ? 1 : -1) + words.length) % words.length;
    return cur.mys ? ((cur = { i: n, j: pickOf(n), mys: true }), paint()) : pickLane(n);
  }
  if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
    e.preventDefault();
    cur.mys = false;
    cur.j = Math.max(
      0,
      Math.min(words[cur.i].length - 1, cur.j + (e.key === 'ArrowRight' ? 1 : -1)),
    );
    return paint();
  }
  const ch = gridWord(e.key);
  if (ch.length === 1) {
    e.preventDefault();
    type(ch);
  }
}
